/**
 * Catalogue access for the planner. Epic 3.1 extends this module (size
 * limits, colours, product matching) rather than adding a second one.
 */
import type { DoorTypeId, ProductRow } from '@planner/config';
import { doorPreviewColour, doorTypeIds } from '@planner/config';
import { parseDoorCatalogue, sizeBandContains, type ManufacturedSize } from '@planner/shared/doors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiBaseUrl, region } from './env.js';
import { regionFetch, useRegionAvailability } from './region-availability.js';
import { track } from './analytics/index.js';

type PricedRow = Pick<ProductRow, 'doorType' | 'Active' | 'retailPrice'>;

/**
 * Cheapest active price per door type. Catalogue prices are already
 * GST-inclusive. A type with no usable price is `undefined`, never zero.
 */
export function fromPrices(rows: readonly PricedRow[]): Record<DoorTypeId, number | undefined> {
  const prices = {} as Record<DoorTypeId, number | undefined>;
  for (const type of doorTypeIds) {
    const usable = rows
      .filter((row) => row.doorType === type && row.Active && row.retailPrice > 0)
      .map((row) => row.retailPrice);
    prices[type] = usable.length > 0 ? Math.min(...usable) : undefined;
  }
  return prices;
}

export type DoorPrice = { status: 'estimate' | 'from'; price: number } | { status: 'unavailable' };

function matchingColourPrices(
  rows: readonly ProductRow[],
  type: DoorTypeId,
  size: ManufacturedSize,
): Map<string, Set<number>> {
  const prices = new Map<string, Set<number>>();
  for (const row of rows) {
    if (
      row.Active &&
      row.doorType === type &&
      sizeBandContains(row, size) &&
      Number.isFinite(row.retailPrice) &&
      row.retailPrice > 0
    ) {
      const colourPrices = prices.get(row.frameColour) ?? new Set<number>();
      colourPrices.add(row.retailPrice);
      prices.set(row.frameColour, colourPrices);
    }
  }
  return prices;
}

/** Unambiguous, active prices for each colour at the manufactured size. */
export function colourPricesForSize(
  rows: readonly ProductRow[],
  type: DoorTypeId,
  size: ManufacturedSize,
): ReadonlyMap<string, number> {
  const result = new Map<string, number>();
  for (const [colour, prices] of matchingColourPrices(rows, type, size)) {
    if (prices.size === 1) {
      result.set(colour, Math.min(...prices));
    }
  }
  return result;
}

/** Indicative price for this size, without selecting a colour or SKU. */
export function estimateDoorPrice(
  rows: readonly ProductRow[],
  type: DoorTypeId,
  size: ManufacturedSize,
): DoorPrice {
  const prices = matchingColourPrices(rows, type, size);
  const previewPrices = prices.get(doorPreviewColour);
  if (previewPrices && previewPrices.size > 1) {
    return { status: 'unavailable' };
  }
  if (previewPrices?.size === 1) {
    return { status: 'estimate', price: Math.min(...previewPrices) };
  }
  if ([...prices.values()].some((colourPrices) => colourPrices.size > 1) || prices.size === 0) {
    return { status: 'unavailable' };
  }
  return {
    status: 'from',
    price: Math.min(...[...prices.values()].flatMap((values) => [...values])),
  };
}

/** Validate the normalized API response without coercing malformed bands. */
export function parseProductData(value: unknown, regionCode: string): ProductRow[] {
  return parseDoorCatalogue(value, regionCode).rows;
}

export async function fetchProductCatalogue() {
  const response = await regionFetch(
    `${apiBaseUrl}/products/${encodeURIComponent(region.code)}/products`,
  );
  if (!response.ok) {
    throw new Error(`Could not load products (${response.status})`);
  }
  const body: unknown = await response.json();
  return parseDoorCatalogue(body, region.code);
}

async function fetchProducts(): Promise<ProductRow[]> {
  return (await fetchProductCatalogue()).rows;
}

/** The active region's product catalogue. */
const reportedFailures = new WeakMap<object, number>();

export function reportCatalogueFailure(): void {
  if (!useRegionAvailability.getState().unavailable) {
    track('error_occurred', {
      context: 'catalogue_load',
      message: 'Catalogue could not be loaded',
    });
  }
}

export function useProducts() {
  const client = useQueryClient();
  const queryKey = ['products', region.code];
  const result = useQuery({
    queryKey,
    queryFn: fetchProducts,
    // Avoid flashing back to "loading" when a customer returns to a Step.
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    const query = client.getQueryCache().find({ queryKey: ['products', region.code] });
    if (
      result.isError &&
      !result.isFetching &&
      query &&
      reportedFailures.get(query) !== query.state.errorUpdateCount
    ) {
      reportedFailures.set(query, query.state.errorUpdateCount);
      reportCatalogueFailure();
    }
  }, [client, result.isError, result.isFetching, result.errorUpdatedAt]);
  return result;
}
